/**
 * Gamification Permission Constants
 * 
 * These permissions control access to gamification features in the admin panel.
 * They should match the permission names defined in the backend.
 */

export const GamificationPermissions = {
  // Wallet Permissions
  Wallet: {
    View: 'Gamification.Wallet.View',
    ViewDetails: 'Gamification.Wallet.ViewDetails',
    AdminAdjust: 'Gamification.Wallet.AdminAdjust',
    BulkAdjust: 'Gamification.Wallet.BulkAdjust',
    ViewAuditLog: 'Gamification.Wallet.ViewAuditLog',
    ViewAnalytics: 'Gamification.Wallet.ViewAnalytics',
    Export: 'Gamification.Wallet.Export',
  },

  // Reward Actions Permissions
  RewardActions: {
    View: 'Gamification.RewardActions.View',
    Create: 'Gamification.RewardActions.Create',
    Edit: 'Gamification.RewardActions.Edit',
    Delete: 'Gamification.RewardActions.Delete',
    Toggle: 'Gamification.RewardActions.Toggle',
    ViewClaims: 'Gamification.RewardActions.ViewClaims',
  },

  // Check-In Permissions
  CheckIn: {
    View: 'Gamification.CheckIn.View',
    ManageMilestones: 'Gamification.CheckIn.ManageMilestones',
    CreateMilestone: 'Gamification.CheckIn.CreateMilestone',
    EditMilestone: 'Gamification.CheckIn.EditMilestone',
    DeleteMilestone: 'Gamification.CheckIn.DeleteMilestone',
    ViewAnalytics: 'Gamification.CheckIn.ViewAnalytics',
  },

  // Rewards (Prize Draws) Permissions
  Rewards: {
    View: 'Gamification.Rewards.View',
    Create: 'Gamification.Rewards.Create',
    Edit: 'Gamification.Rewards.Edit',
    Delete: 'Gamification.Rewards.Delete',
    ExecuteDraw: 'Gamification.Rewards.ExecuteDraw',
    ViewParticipants: 'Gamification.Rewards.ViewParticipants',
    ViewWinners: 'Gamification.Rewards.ViewWinners',
    ManageWinners: 'Gamification.Rewards.ManageWinners',
  },

  // Referral Management Permissions
  Referrals: {
    View: 'Engagement.Referrals.View',
    Edit: 'Engagement.Referrals.Edit',
    ViewStats: 'Engagement.Referrals.ViewStats',
  },

  // General Gamification Permissions
  General: {
    View: 'Gamification.View',
    Manage: 'Gamification.Manage',
    ViewOverview: 'Gamification.ViewOverview',
  },
} as const;

/**
 * Legacy permission names for backward compatibility
 * Map old permission names to new ones
 */
export const LegacyPermissionMap: Record<string, string> = {
  'Admin.Gamification.View': GamificationPermissions.General.View,
  'WalletPermissions.CoinWallet.AdminAdjust': GamificationPermissions.Wallet.AdminAdjust,
};

/**
 * Helper to get permission name with legacy fallback
 */
export function getPermissionName(permission: string): string {
  return LegacyPermissionMap[permission] || permission;
}

/**
 * Common permission groups for UI components
 */
export const PermissionGroups = {
  WalletAdmin: [
    GamificationPermissions.Wallet.AdminAdjust,
    GamificationPermissions.Wallet.BulkAdjust,
  ],
  WalletViewer: [
    GamificationPermissions.Wallet.View,
    GamificationPermissions.Wallet.ViewDetails,
  ],
  RewardActionsManager: [
    GamificationPermissions.RewardActions.Create,
    GamificationPermissions.RewardActions.Edit,
    GamificationPermissions.RewardActions.Delete,
    GamificationPermissions.RewardActions.Toggle,
  ],
  CheckInManager: [
    GamificationPermissions.CheckIn.ManageMilestones,
    GamificationPermissions.CheckIn.CreateMilestone,
    GamificationPermissions.CheckIn.EditMilestone,
    GamificationPermissions.CheckIn.DeleteMilestone,
  ],
  RewardsManager: [
    GamificationPermissions.Rewards.Create,
    GamificationPermissions.Rewards.Edit,
    GamificationPermissions.Rewards.Delete,
    GamificationPermissions.Rewards.ExecuteDraw,
    GamificationPermissions.Rewards.ManageWinners,
  ],
  ReferralManager: [
    GamificationPermissions.Referrals.View,
    GamificationPermissions.Referrals.Edit,
  ],
} as const;
